import {
  isSameContent,
  MergeConflicts,
  mergeValue,
} from '@superfunnel/utils/agent-changes';

type Item = { id?: unknown } & Record<string, unknown>;
type Doc = Record<string, unknown>;

const clone = <T>(value: T): T =>
  value === undefined ? value : JSON.parse(JSON.stringify(value));

const byId = (items: Item[]) => new Map(items.map((item) => [item.id, item]));

const restoreOrder = (items: Item[], beforeOrder: unknown[]): Item[] => {
  const ordered = new Set(beforeOrder);
  const queue = beforeOrder
    .map((id) => items.find((item) => item.id === id))
    .filter((item): item is Item => !!item);
  return items.map((item) => (ordered.has(item.id) ? queue.shift()! : item));
};

const revertCollection = (
  current: Item[],
  before: Item[],
  after: Item[],
  conflicts: MergeConflicts,
): Item[] => {
  const beforeById = byId(before);
  const afterById = byId(after);

  let result = current.flatMap((item): Item[] => {
    const was = beforeById.get(item.id);
    const became = afterById.get(item.id);
    if (!was && became) {
      if (isSameContent(item, became)) return [];
      conflicts.count += 1;
      return [item];
    }
    if (!was || !became) return [item];
    return [mergeValue(item, became, was, conflicts) as Item];
  });

  before.forEach((item, index) => {
    if (afterById.has(item.id) || result.some((r) => r.id === item.id)) return;
    const previousId = before[index - 1]?.id;
    const anchor = result.findIndex((r) => r.id === previousId);
    result.splice(anchor + 1, 0, clone(item));
  });

  const sharedBefore = before
    .map((item) => item.id)
    .filter((id) => afterById.has(id));
  const sharedAfter = after
    .map((item) => item.id)
    .filter((id) => beforeById.has(id));
  if (!isSameContent(sharedBefore, sharedAfter)) {
    const shared = new Set(sharedAfter);
    const sharedNow = result
      .map((item) => item.id)
      .filter((id) => shared.has(id));
    if (isSameContent(sharedNow, sharedAfter)) {
      result = restoreOrder(result, sharedBefore);
    } else if (!isSameContent(sharedNow, sharedBefore)) {
      conflicts.count += 1;
    }
  }

  return result;
};

/**
 * Undo one change on the current document by reverting only what that change
 * touched (the difference between `before` and `after`). Anything written
 * since, by the agent, another user or a later edit, is kept; a part that was
 * edited again since is left as is and counted in `conflicts`.
 */
export function revertTouched<T extends object>(
  current: T,
  before: T,
  after: T,
  idKeyedCollections: string[],
  conflicts: MergeConflicts = new MergeConflicts(),
): MergeConflicts {
  const target = current as Doc;
  const was = before as Doc;
  const became = after as Doc;

  for (const key of new Set([...Object.keys(was), ...Object.keys(became)])) {
    if (isSameContent(was[key], became[key])) continue;

    const reverted = idKeyedCollections.includes(key)
      ? revertCollection(
          (target[key] as Item[] | undefined) ?? [],
          (was[key] as Item[] | undefined) ?? [],
          (became[key] as Item[] | undefined) ?? [],
          conflicts,
        )
      : mergeValue(target[key], became[key], was[key], conflicts);

    if (reverted === undefined) delete target[key];
    else target[key] = reverted;
  }

  return conflicts;
}
